import { noop } from "lodash";
import moment from "moment";
import RecordRTC from "recordrtc";

export interface IrecordRtcUtilsParams {
    recordrtcOptions: any;
    includeAudio?: boolean;
    includeVideo?: boolean;
    onDurationChange?: Function;
}

export interface IrecordRtcUtils {
    getRecorder: Function;
    initRecorder: Function;
    start: Function;
    stop: Function;
    pause: Function;
    resume: Function;
    reset: Function;
    destroy: Function;
    getState: Function;
    getRecordingAsBlob: Function;
    getRecordingAsFile: Function;
    getDuration: Function;
}

export const RECORD_RTC_RECORDER_STATES = {
    RECORDING: "recording",
    PAUSED: "paused",
    STOPPED: "stopped",
    INACTIVE: "inactive",
};

let mediaStream = null;
let rtcRecorder = null;
let timerID = null;
let recordingDuration = 0;

export const recordRtcUtils: (
    params: IrecordRtcUtilsParams
) => IrecordRtcUtils = (params) => {
    const {
        recordrtcOptions,
        includeAudio = true,
        includeVideo = false,
        onDurationChange = noop,
    } = params;

    const initRecorder = (stream: MediaStream) => {
        mediaStream = stream;

        const recorder = setRecorder(
            new RecordRTC(stream, {
                recorderType: RecordRTC.MediaStreamRecorder,
                ...recordrtcOptions,
            })
        );

        timerID = null;
        recordingDuration = 0;

        return recorder;
    };

    const getRecorder = () => {
        return rtcRecorder;
    };

    const setRecorder = (recorderInstance) => {
        rtcRecorder = recorderInstance;

        return rtcRecorder;
    };

    const stopMediaStream = () => {
        if (!!mediaStream) {
            mediaStream.stop();
        }
    };

    const startTimer = () => {
        timerID = setInterval(() => {
            const recorder = getRecorder();

            if (recorder?.getState() === RECORD_RTC_RECORDER_STATES.RECORDING) {
                recordingDuration += 1000;
                onDurationChange(getDuration());
            }
        }, 1000);
    };

    const stopTimer = () => {
        clearInterval(timerID);
        recordingDuration = 0;

        timerID = null;
    };

    const start = (onStart: Function = noop) => {
        if (!!getRecorder()) {
            destroy();
        }

        navigator.mediaDevices
            .getUserMedia({ audio: includeAudio, video: includeVideo })
            .then((stream) => {
                const recorder = initRecorder(stream);

                recorder.startRecording();
                startTimer();
                onStart();
            })
            .catch((error) => {
                throw error;
            });
    };

    const stop = (onStop: Function = noop) => {
        const recorder = getRecorder();

        if (!!recorder) {
            recorder.stopRecording();
        }

        stopMediaStream();
        stopTimer();
        onStop();
    };

    const pause = (onPause: Function = noop) => {
        const recorder = getRecorder();

        recorder.pauseRecording();
        onPause();
    };

    const resume = (onResume: Function = noop) => {
        const recorder = getRecorder();

        recorder.resumeRecording();
        onResume();
    };

    const getRecordingAsBlob = () => {
        const recorder = getRecorder();
        let blob = recorder.getBlob();

        return blob;
    };

    const getRecordingAsFile = (fileName: string) => {
        let blob = getRecordingAsBlob();

        return new File([blob], fileName, { type: blob.type });
    };

    const reset = (onReset: Function = noop) => {
        const recorder = getRecorder();

        recorder.reset();
        
        recordingDuration = 0;

        onDurationChange(getDuration());
        stopMediaStream();
        onReset();
    };

    const destroy = (onDestroy: Function = noop) => {
        const recorder = getRecorder();

        stopTimer();

        if (!!recorder) {
            recorder.destroy();
            setRecorder(null);
        }

        stopMediaStream();

        onDestroy();
    };

    const getDuration = () => {
        const momentDuration = moment.duration(recordingDuration);
        const hours = momentDuration
            .hours()
            .toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: false });
        const minutes = momentDuration
            .minutes()
            .toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: false });
        const seconds = momentDuration
            .seconds()
            .toLocaleString("en-US", { minimumIntegerDigits: 2, useGrouping: false });

        return `${hours} : ${minutes} : ${seconds}`;
    };

    const getState = () => {
        const recorder = getRecorder();

        return recorder?.getState() || null;
    };

    return {
        getRecorder,
        initRecorder,
        start,
        stop,
        pause,
        resume,
        reset,
        destroy,
        getState,
        getRecordingAsBlob,
        getRecordingAsFile,
        getDuration,
    };
};
